import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, Package } from 'lucide-react';
import { MyContext } from '../context';
import Modal from './modal';
import { createStore, addStorePaymentMethod } from '../utils/queries';
import { padCustomerNumber, lookupPaceCustomerBundle, applyPaceCustomerDefaults } from '../utils/paceOnboarding';

const initialCore = { storeName: '', customerNumber: '' };

// The store code is an internal slug (X-Store-Code header value), not
// something an admin has any reason to invent -- generate it from the store
// name plus a stable suffix (the ERP customer number when connected, a
// random one otherwise) so it's still unique and human-readable without
// asking for input.
const slugify = (value) =>
	value
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.slice(0, 40);

const randomSuffix = () => Math.random().toString(36).slice(2, 8);

const buildStoreCode = (storeName, suffix) => `${slugify(storeName) || 'store'}-${suffix}`;

export default function CreateStoreWizard({ open, setOpen }) {
	const { refreshStores, setActiveStoreId } = useContext(MyContext);
	const navigate = useNavigate();

	const [step, setStep] = useState('choice');
	const [core, setCore] = useState(initialCore);
	const [creating, setCreating] = useState(false);
	const [createError, setCreateError] = useState('');

	const reset = () => {
		setStep('choice');
		setCore(initialCore);
		setCreating(false);
		setCreateError('');
	};

	const close = () => {
		setOpen(false);
		reset();
	};

	const chooseFulfillment = (pace) => {
		setStep(pace ? 'paceCustomer' : 'coreDetails');
	};

	const finishAndRedirect = async (storeId) => {
		await refreshStores();
		setActiveStoreId(storeId);
		close();
		navigate('/settings/setup');
	};

	// ERP path: as soon as the customer is found, create the store and
	// auto-apply every sensible default in one shot -- no inline
	// confirm-defaults/shipping/payment-method steps. The admin reviews/
	// adjusts any of this afterward on the Finish Setup page.
	const handleLookup = async () => {
		if (!core.customerNumber) return;
		const customerNumber = padCustomerNumber(core.customerNumber);
		setCore((c) => ({ ...c, customerNumber }));
		setCreating(true);
		setCreateError('');
		try {
			const bundle = await lookupPaceCustomerBundle(customerNumber);
			if (!bundle.success) {
				setCreateError(bundle.error);
				return;
			}
			const { customer } = bundle.data;

			const storeName = customer.name || `Customer ${customerNumber}`;
			const storeCode = buildStoreCode(storeName, customerNumber);

			const createResult = await createStore({ storeCode, storeName, erpCustomerNumber: customerNumber });
			if (!createResult?.success) {
				setCreateError(createResult?.error?.message || 'Failed to create store.');
				return;
			}
			const storeId = createResult.data.storeId;

			await applyPaceCustomerDefaults(storeId, customerNumber, bundle.data);

			await finishAndRedirect(storeId);
		} finally {
			setCreating(false);
		}
	};

	// Standalone path: only a store name is needed (no ERP data to derive
	// anything from) -- create immediately and land on the same Finish Setup
	// page as the ERP path.
	const handleCreateStandalone = async () => {
		if (!core.storeName) return;
		setCreating(true);
		setCreateError('');
		try {
			const storeCode = buildStoreCode(core.storeName, randomSuffix());
			const createResult = await createStore({ storeCode, storeName: core.storeName });
			if (!createResult?.success) {
				setCreateError(createResult?.error?.message || 'Failed to create store.');
				return;
			}
			const storeId = createResult.data.storeId;
			await addStorePaymentMethod(storeId, 'CREDIT_CARD');
			await finishAndRedirect(storeId);
		} finally {
			setCreating(false);
		}
	};

	return (
		<Modal open={open} setOpen={close} title='New Store' size='md'>
			{step === 'choice' && (
				<div className='space-y-3'>
					<p className='text-sm text-gray-500 mb-2'>Does this store fulfill orders through ERP?</p>
					<button
						type='button'
						onClick={() => chooseFulfillment(true)}
						className='w-full flex items-start gap-3 px-4 py-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50'
					>
						<Package className='size-5 shrink-0 mt-0.5 text-gray-600' />
						<span>
							<span className='block font-medium text-sm'>Connects to ERP</span>
							<span className='block text-sm text-gray-500 mt-0.5'>
								Integrate ERP ERP customer details, shipping, and payment terms.
							</span>
						</span>
					</button>
					<button
						type='button'
						onClick={() => chooseFulfillment(false)}
						className='w-full flex items-start gap-3 px-4 py-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50'
					>
						<Globe className='size-5 shrink-0 mt-0.5 text-gray-600' />
						<span>
							<span className='block font-medium text-sm'>Standalone store</span>
							<span className='block text-sm text-gray-500 mt-0.5'>
								No ERP/ERP fulfillment. Products and orders are managed manually.
							</span>
						</span>
					</button>
				</div>
			)}

			{step === 'paceCustomer' && (
				<div className='space-y-4'>
					<div>
						<label>ERP ERP Customer Number</label>
						{/* <p className='text-xs text-gray-400 mb-1'>Padded to 8 characters automatically, e.g. "1656" becomes "00001656".</p> */}
						<div className='flex gap-2'>
							<input
								className='form-input'
								autoFocus
								placeholder='00001234'
								disabled={creating}
								value={core.customerNumber}
								onChange={(e) => {
									setCore({ ...core, customerNumber: e.target.value });
									setCreateError('');
								}}
								onKeyDown={(e) => {
									if (e.key === 'Enter' && !creating && core.customerNumber) handleLookup();
								}}
							/>
							<button
								type='button'
								className='btn-primary shrink-0'
								disabled={creating || !core.customerNumber}
								onClick={handleLookup}
							>
								{creating ? 'Creating store...' : 'Look up'}
							</button>
						</div>
						{creating && (
							<p className='text-xs text-gray-500 mt-1'>Looking up the customer and setting up defaults in ERP -- this can take a moment.</p>
						)}
						{createError && <p className='text-sm text-pi-red mt-1'>{createError}</p>}
					</div>

					<div className='pt-4'>
						<button type='button' className='btn-secondary' onClick={() => setStep('choice')} disabled={creating}>
							Back
						</button>
					</div>
				</div>
			)}

			{step === 'coreDetails' && (
				<div className='space-y-4'>
					<div>
						<label>Store Name</label>
						<input
							className='form-input'
							autoFocus
							disabled={creating}
							value={core.storeName}
							onChange={(e) => setCore({ ...core, storeName: e.target.value })}
							onKeyDown={(e) => {
								if (e.key === 'Enter' && !creating && core.storeName) handleCreateStandalone();
							}}
						/>
					</div>

					{createError && <p className='text-sm text-pi-red'>{createError}</p>}

					<div className='flex justify-between pt-4'>
						<button type='button' className='btn-secondary' onClick={() => setStep('choice')} disabled={creating}>
							Back
						</button>
						<button
							type='button'
							className='btn-primary'
							disabled={creating || !core.storeName}
							onClick={handleCreateStandalone}
						>
							{creating ? 'Creating...' : 'Create Store'}
						</button>
					</div>
				</div>
			)}
		</Modal>
	);
}
